import Phaser from 'phaser';
import { AudioManager } from '../audio/AudioManager';
import { ProgressData as PlayerData } from '../systems/ProgressData';
import { mountMenu, element, button, primaryShortcut } from '../util/menuUI';
import './MenuScene.css';
import { studentAvatarBase } from '../avatar/AvatarConfig';

export class MenuScene extends Phaser.Scene {
  constructor() { super('MenuScene'); }
  create() {
    const student = this.registry.get('studentIdentity');
    if (student && !studentAvatarBase(student)) { this.scene.start('ProfileScene', { source: 'student' }); return; }
    const root = mountMenu(this, `tmag-home tmag-launch-hub${student ? '' : ' tmag-experience-hub'}`);
    // Ambos personajes presentan la aventura. En la misión se usa solo el elegido en el perfil.
    const explorador = element('img', 'tmag-home-hero tmag-home-hero-explorador');
    explorador.src = '/assets/mateo-astronauta-portada.png';
    explorador.alt = 'Astronauta explorador de TechnoMathPR';

    const exploradora = element('img', 'tmag-home-hero tmag-home-hero-exploradora');
    exploradora.src = '/assets/aurymar-astronauta-portada.png';
    exploradora.alt = 'Astronauta exploradora de TechnoMathPR';
    root.append(explorador, exploradora);

    const content = element('div', 'tmag-home-content');
    const brand = element('h1', 'tmag-brand');
    brand.setAttribute('aria-label', 'TechnoMathPR');
    const brandMain = element('span', 'tmag-brand-main', 'TechnoMath');
    if (student) brandMain.append(element('span', 'tmag-student-logo-pr', 'PR'));
    brand.append(
      brandMain,
      ...(!student ? [element('span', 'tmag-home-logo-pr', 'PR')] : []),
    );
    content.append(brand);
    content.append(element('p', 'tmag-series', '★ AVENTURA GALÁCTICA ★'));
    content.append(element('p', 'tmag-edition', 'OPERACIONES ARITMÉTICAS'));
    content.append(element(
      'p',
      'tmag-home-description',
      'Explora cuatro planetas y domina la suma, resta, multiplicación y división con números naturales.',
    ));

    if (student) {
      const slot = element('div', 'tmag-student-identity-slot');
      slot.dataset.avatarBase = studentAvatarBase(student);
      slot.dataset.studentId = student.student_id;
      root.append(slot);
    }
    else {
      const crew = element('div', 'tmag-experience-crew');
      crew.append(explorador, exploradora);
      content.append(crew);
    }

    const start = () => {
      if (this.menuLeaving) return;
      this.menuLeaving = true;
      let audio = this.registry.get('audio');
      if (!audio) {
        audio = new AudioManager(PlayerData.getSettings().muted);
        this.registry.set('audio', audio);
      }
      audio.ensure();
      audio.startMusic();
      if (student) this.scene.start('GameModeScene');
      else this.scene.start('ProfileScene', { source: 'home' });
    };

    if (student) {
      content.append(button('▶ JUGAR', start, 'tmag-button tmag-primary tmag-launch-button tmag-home-play'));
    } else {
      const prompt = element('p', 'tmag-experience-question');
      prompt.append(experienceIcon('planet'), element('span', '', process.env.REACT_APP_ACCOUNTS_V1_ENABLED === 'true'
        ? '¿QUÉ EXPERIENCIA QUIERES?' : '¡COMIENZA TU AVENTURA!'));
      content.append(prompt);
      const play = button('', start, 'tmag-button tmag-primary tmag-launch-button tmag-home-play');
      play.append(experienceIcon('rocket'), element('span', 'tmag-experience-play-copy'));
      play.lastChild.append(element('strong', '', 'QUIERO JUGAR'), element('span', '', 'Empieza gratis, sin cuenta'));
      const arrow = element('span', 'tmag-experience-arrow', '→');
      arrow.setAttribute('aria-hidden', 'true');
      play.append(arrow);
      content.append(play);
      if (process.env.REACT_APP_ACCOUNTS_V1_ENABLED === 'true') {
        const access = element('nav', 'tmag-experience-paths');
        access.setAttribute('aria-label', 'Elige tu experiencia');
        for (const [kind, title, description, href] of [
          ['student', 'SOY ESTUDIANTE', 'Tengo un código de mi clase', '/student'],
          ['family', 'SOY FAMILIA', 'Acompañar el progreso de mi hijo', '/account?entry=family'],
          ['teacher', 'SOY MAESTRO/A', 'Crear y administrar mi clase', '/account?entry=teacher'],
        ]) {
          const link = element('a', `tmag-experience-card tmag-experience-card--${kind}`);
          link.href = href;
          const titleNode = element('strong', '', title);
          const arrow = element('span', 'tmag-experience-arrow', '→');
          arrow.setAttribute('aria-hidden', 'true');
          link.append(experienceIcon(kind), titleNode, element('span', 'tmag-experience-description', description), arrow);
          access.append(link);
        }
        content.append(access);
      }
    }
    const origin = element('div', 'tmag-origin');
    const flag = element('span', 'tmag-origin-flag');
    flag.setAttribute('role', 'img');
    flag.setAttribute('aria-label', 'Bandera de Puerto Rico');
    origin.append(
      flag,
      element('p', 'tmag-origin-title', 'CREADO EN PUERTO RICO'),
      element('p', 'tmag-origin-copyright', '© 2026 TechnoMathPR. Todos los derechos reservados.'),
    );
    content.append(origin);
    root.append(content);
    primaryShortcut(root, start);
  }
}

// Decorative local SVGs; all labels and controls remain native, accessible DOM.
function experienceIcon(kind) {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  for (const [name, value] of Object.entries({ viewBox: '0 0 64 64', fill: 'currentColor', 'aria-hidden': 'true', focusable: 'false' })) svg.setAttribute(name, value);
  svg.classList.add('tmag-experience-icon');
  const shapes = {
    student: 'M3 20 32 9l29 11-29 12L3 20Zm11 9v15c12 9 24 9 36 0V29L32 37 14 29ZM7 25v25h4V26H7Z',
    family: 'M22 17a10 10 0 1 0 20 0 10 10 0 1 0-20 0ZM4 23a7 7 0 1 0 14 0 7 7 0 1 0-14 0Zm42 0a7 7 0 1 0 14 0 7 7 0 1 0-14 0ZM20 35q12-10 24 0v21H20V35ZM3 38q7-8 14-2v20H3V38Zm44-2q7-6 14 2v18H47V36Z',
    teacher: 'M28 5h32v30H37v-4h19V9H28V5ZM7 22a8 8 0 1 0 16 0 8 8 0 1 0-16 0ZM3 39q9-8 21-1l17-13 3 4-17 16v13H3V39ZM42 39l-8 17 4 2 8-16 8 16 4-2-8-17h-8Z',
    planet: 'M20 22a15 15 0 1 0 24 18 15 15 0 1 0-24-18ZM5 45c-8-12 41-34 54-29l-1 5C48 16 7 37 10 42c4 6 45-15 45-21l4-5C77 28 12 58 5 45Z',
    rocket: 'M26 34C28 15 43 4 59 5c1 16-10 31-29 33l-4-4Zm17-15a6 6 0 1 0 12 0 6 6 0 1 0-12 0ZM22 26l-13 3-5 14 18-5V26Zm13 15-9 1-5 18 14-5V41ZM17 44l7 7-17 9 10-16Z',
  };
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', shapes[kind]);
  svg.append(path);
  return svg;
}
